import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import CustomDatePicker from "../../common/CustomDatePicker";
import CustomInputField from "../../common/CustomInputField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchActivePracticeProvidersData,
  usePracticeProvidersSlice,
} from "../../../redux/slice/practice/practiceProvidersSlice";
import {
  AddPatientByEMRCheck,
  addPatientThorughEMR,
  getSurgicalTemplateBasedOnProvider,
  resetSurgicalTemplateOptions,
  usePatientReducer,
} from "../../../redux/slice/patient/patientSlice";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import { Option } from "../../operation/practice/PracticeEditView";
import { fullName } from "../../../utils/Utility";
import {
  MSG_PATIENT_NOT_FOUND_IN_EMR,
  PRACTICE_TYPE,
  RESPONSE_CODES,
} from "../../../constants/applicationConstants";
import {
  getPracticesByUserRoleUserSlice,
  useUserReducer,
} from "../../../redux/slice/userSlice";

interface AddPatientModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
}

const AddPatientModal = ({ open, onClose }: AddPatientModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { practiceProvidersInfo } = usePracticeProvidersSlice();
  const { surgicalTemplateOptions } = usePatientReducer();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [eMRIntegrationProviderList, setEMRIntegrationProviderList] = useState<
    CodeTable[]
  >([]);
  const [showOptionPopup, setShowOptionsPopup] =
    useState<OptionPopupObjectProps>({ message: "", show: false });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<AddPatientByEMRCheck>({ mode: "onSubmit" });

  const watchedSpecialization = watch("practiceSpecialization");

  useEffect(() => {
    if (open) {
      if (
        !practiceByUserRoleUserSlice ||
        practiceByUserRoleUserSlice.length === 0
      ) {
        dispatch(getPracticesByUserRoleUserSlice(true));
      }
      dispatch(resetSurgicalTemplateOptions(null));
    }
  }, [open]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sorted = [
        ...getCodeTableValuesByTableName(
          allCodeTablesValuesMap,
          "EMRIntegrationProvider",
        ),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setEMRIntegrationProviderList(sorted);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (getValues("practiceId")) {
      handlePracticeChange({ value: getValues("practiceId") });
      dispatch(resetSurgicalTemplateOptions(null));
    }
  }, [getValues("practiceId"), getValues("providerId")]);

  useEffect(() => {
    if (getValues("providerId")) {
      handleProviderChange({ value: getValues("providerId") });
      dispatch(resetSurgicalTemplateOptions(null));
    }
  }, [getValues("providerId")]);

  const handlePracticeChange = (data: any) => {
    if (data?.value !== undefined && data?.value !== null) {
      dispatch(
        fetchActivePracticeProvidersData({
          productId: String(data?.value),
          onlyActiveRecords: true,
        }),
      );
      const practiceEmrData = practiceByUserRoleUserSlice?.find(
        (p: any) => p.practiceId === data?.value,
      );
      setValue(
        "emrProviderPracticeId",
        practiceEmrData?.emrProviderPracticeId ?? null,
      );
      setValue(
        "emrIntegrationProvider",
        practiceEmrData?.emrIntegrationProvider,
      );
      setValue("practiceSpecialization", practiceEmrData?.specialization);
    } else {
      setValue("emrIntegrationProvider", null);
      dispatch(resetSurgicalTemplateOptions(null));
      reset({ emrProviderPracticeId: null });
    }
  };

  const handleProviderChange = (data: any) => {
    if (data?.value !== undefined && data?.value !== null) {
      dispatch(getSurgicalTemplateBasedOnProvider(data?.value));
    } else {
      setValue("providerId", null);
      setValue("providerTemplateId", null);
      dispatch(resetSurgicalTemplateOptions(null));
    }
  };

  const cancelOptionPopup = () =>
    setShowOptionsPopup({ message: "", show: false });

  const practiceOptions = useMemo(() => {
    if (!practiceByUserRoleUserSlice?.length) return [];

    const sorted = [...practiceByUserRoleUserSlice]
      .map((p: any) => ({
        value: p.practiceId,
        label: p.practiceName,
        emrProviderPracticeId: p.emrProviderPracticeId,
        emrIntegrationProvider: p.emrIntegrationProvider,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (sorted.length === 1) {
      setValue("practiceId", Number(sorted[0].value));
      setValue("emrProviderPracticeId", sorted[0].emrProviderPracticeId);
      setValue("emrIntegrationProvider", sorted[0].emrIntegrationProvider);
    }

    return sorted.map(({ value, label }) => ({ value, label }));
  }, [practiceByUserRoleUserSlice, setValue]);

  const practiceProvidersOptions = useMemo(() => {
    const isSurgical =
      getValues("practiceSpecialization") === PRACTICE_TYPE.SURGICAL ||
      getValues("practiceSpecialization") ===
        PRACTICE_TYPE.WOUND_CARE_AND_SURGICAL;

    const options: { label: string; value: number }[] = [];
    practiceProvidersInfo?.forEach((opt: any) => {
      options.push({
        label: `${opt.title ?? ""} ${opt.firstName ?? ""} ${
          opt.middleName ? opt.middleName + " " : ""
        }${opt.lastName ?? ""}`.trim(),
        value: opt.providerId,
      });
    });

    if (isSurgical && options.length === 1) {
      setValue("providerId", options[0].value);
      setValue("providerName", options[0].label);
    }
    return options;
  }, [practiceProvidersInfo, watchedSpecialization, PRACTICE_TYPE]);

  const surgicalTemplateOptionData = useMemo(() => {
    if (!surgicalTemplateOptions?.length) return [];
    return surgicalTemplateOptions
      .filter((opt: any) => opt.inactiveDate === null)
      .map((opt: any) => ({ label: opt.procedureName, value: opt.templateId }));
  }, [surgicalTemplateOptions]);

  const onSubmit = (data: AddPatientByEMRCheck) => {
    const EMRData = {
      practiceEMRId: data?.emrProviderPracticeId,
      firstName: data?.firstName,
      lastName: data?.lastName,
      dateOfBirth: moment(data?.dateOfBirth?.toString()).format("MM/DD/YYYY"),
      dateOfSurgery: moment(data?.dateOfSurgery?.toString()).format(
        "MM/DD/YYYY",
      ),
      practiceId: data?.practiceId,
      providerId: data?.providerId,
      providerName: getValues("providerName"),
      patientAddedFromEmrPopup: true,
      providerTemplateId: data?.providerTemplateId,
    };

    if (EMRData.practiceEMRId) {
      dispatch(addPatientThorughEMR(EMRData)).then((res: any) => {
        if (
          res?.payload?.code === RESPONSE_CODES.SUCCESS_CODE &&
          res?.payload?.data?.patientId
        ) {
          const patientName = fullName(
            null,
            res?.payload?.data?.firstName,
            res?.payload?.data?.middleName,
            res?.payload?.data?.lastName,
          );
          navigate("/patientDetails", {
            state: {
              patientDetails: {
                patientId: res?.payload?.data?.patientId,
                patientName,
                dob: res?.payload?.birthDate,
              },
              alert: {
                message:
                  "Patient details are received. Please proceed to create IVR.",
                showButton: true,
                buttonText: "CREATE IVR",
              },
            },
          });
        } else if (
          res?.payload?.code === RESPONSE_CODES.SUCCESS_CODE &&
          res?.payload?.message === MSG_PATIENT_NOT_FOUND_IN_EMR
        ) {
          setShowOptionsPopup({
            message:
              "Patient not found in EMR, please enter data manually by clicking on Add Patient.",
            show: true,
            variant: "error",
            buttons: [
              {
                name: "Add Patient",
                color: "success",
                onClick: () =>
                  navigate("/editPatient", {
                    state: {
                      patientDetails: {
                        isPatientEdit: false,
                        patientId: null,
                        patientData: EMRData,
                      },
                    },
                  }),
              },
              {
                name: "Cancel",
                color: "secondary",
                onClick: cancelOptionPopup,
              },
            ],
          });
        }
      });
    } else {
      navigate("/editPatient", {
        state: {
          patientDetails: {
            isPatientEdit: false,
            patientId: null,
            patientData: EMRData,
          },
        },
      });
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => reset(), 300);
  };

  const isSurgical =
    watchedSpecialization === PRACTICE_TYPE.SURGICAL ||
    watchedSpecialization === PRACTICE_TYPE.WOUND_CARE_AND_SURGICAL;

  const providerDisabled =
    (isSurgical && practiceProvidersOptions?.length === 1) ||
    !getValues("practiceId");

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? (
      <Typography sx={{ color: tokens.error, fontSize: "0.75rem", mt: 0.5 }}>
        {msg}
      </Typography>
    ) : null;

  return (
    <>
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={cancelOptionPopup}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <UserPlus size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                fontWeight={700}
                sx={{
                  fontSize: "1.1rem",
                  color: tokens.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                Add Patient
              </Typography>

              {getValues("emrIntegrationProvider") &&
                getValues("emrProviderPracticeId") && (
                  <Tooltip
                    arrow
                    placement="right"
                    title={
                      <>
                        <Typography variant="body2">
                          EMR Provider: {getValues("emrIntegrationProvider")}
                        </Typography>
                        <Typography variant="body2">
                          EMR Practice ID: {getValues("emrProviderPracticeId")}
                        </Typography>
                      </>
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.2,
                        borderRadius: "6px",
                        cursor: "pointer",
                        userSelect: "none",
                        backgroundColor: "rgba(251,146,60,0.12)",
                        border: "1px solid rgba(251,146,60,0.3)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#f97316",
                          letterSpacing: "0.04em",
                        }}
                      >
                        EMR
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
            </Box>

            <Typography
              sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
            >
              Fill in the patient details below
            </Typography>
          </Box>

          <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={bodySx}>
          <Box>
            <FieldLabel label="Practice Name" required />
            <Box sx={dropdownWrapSx(!!errors.practiceId)}>
              <CustomDropdownField
                name="practiceId"
                control={control}
                options={practiceOptions}
                rules={{ required: "Practice Name is required." }}
                size="small"
                placeholderText="Select a practice..."
                sortOptions={false}
                disabled={practiceOptions?.length === 1}
                onChangeSelect={handlePracticeChange}
              />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel label="First Name" required />
              <CustomInputField
                name="firstName"
                control={control}
                controllerName="firstName"
                size="small"
                placeholder="Enter first name"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                sx={inputFieldSx(!!errors.firstName)}
                inputProps={{ style: { color: tokens.textPrimary } }}
                rules={{ required: "First Name is required." }}
              />
            </Box>
            <Box>
              <FieldLabel label="Last Name" required />
              <CustomInputField
                name="lastName"
                control={control}
                controllerName="lastName"
                size="small"
                placeholder="Enter last name"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                sx={inputFieldSx(!!errors.lastName)}
                inputProps={{ style: { color: tokens.textPrimary } }}
                rules={{ required: "Last Name is required." }} 
              />
            </Box>
          </Box>

          <Box>
            <FieldLabel label="Date of Birth" required />
            <Box sx={datepickerWrapSx(!!errors.dateOfBirth)}>
              <CustomDatePicker
                label=""
                control={control}
                controllerName="dateOfBirth"
                rules={{ required: "Date Of Birth is required." }}
                size="small"
                fullWidth
                isDisableFuture
              />
            </Box>
          </Box>

          {isSurgical && (
            <Box>
              <FieldLabel label="Date of Surgery" required />
              <Box sx={datepickerWrapSx(!!errors.dateOfSurgery)}>
                <CustomDatePicker
                  label=""
                  control={control}
                  controllerName="dateOfSurgery"
                  rules={{ required: "Date Of Surgery is required." }}
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          )}

          <Box>
            <FieldLabel label="Provider" required={isSurgical} />
            <Box sx={dropdownWrapSx(!!errors.providerId)}>
              <CustomDropdownField
                name="providerId"
                control={control}
                options={practiceProvidersOptions}
                rules={isSurgical ? { required: "Provider is required." } : {}}
                size="small"
                placeholderText="Select a provider..."
                sortOptions={false}
                disabled={providerDisabled}
                onChangeSelect={handleProviderChange}
              />
            </Box>
          </Box>

          {surgicalTemplateOptionData.length > 0 && (
            <Box>
              <FieldLabel label="Provider Template" required={isSurgical} />
              <Box sx={dropdownWrapSx(!!errors.providerTemplateId)}>
                <CustomDropdownField
                  name="providerTemplateId"
                  control={control}
                  options={surgicalTemplateOptionData}
                  rules={
                    isSurgical
                      ? { required: "Surgical Template is required." }
                      : {}
                  }
                  size="small"
                  placeholderText="Select a template..."
                  sortOptions={true}
                  disabled={!getValues("providerId")}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                <PersonAddAlt1Icon sx={{ fontSize: "1rem !important" }} />
              }
              onClick={handleSubmit(onSubmit)}
              sx={primaryBtnSx}
            >
              Add Patient
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default AddPatientModal;
